import React from 'react';
import { Amplify } from '@aws-amplify/core';
import { config as amplifyConfig } from '@/amplify';
import { initSentry } from '@/utils/SentrySetup';
import { initPostHog } from '@/utils/analytics/posthog';
import { registerManagerTaxonomy } from '@/utils/analytics/managerTaxonomy';
import { createRoot } from 'react-dom/client';
import ServiceWorkerLoader from '@/modules/app/ServiceWorkerLoader';
import { Router } from 'react-router';
import { createBrowserHistory } from 'history';
import GraphQLProvider from '@/graphql/graphql';
import { AuthMachineProvider } from '@/utils/auth';
import { listenForHostedUIFailure } from '@/utils/hostedUIFailure';
import { restoreDestinationAfterSignOut } from '@/utils/destinationAcrossSignOut';
import {
  ShortcutsProvider,
  KeyboardHelpModal,
} from '@/hooks/KeyboardShortcuts';
import App from './AppManager';
import { InstallProvider } from '@/hooks/usePWAInstall';
import './react_11538_workaround';
import AppErrorBoundary from '@/components/AppErrorBoundary';
import { Provider as JotaiProvider } from 'jotai';
import { BilderitRbacManager } from '@/hooks/BilderitRbacProvider';
import { WebPushHandler } from '@/utils/WebPushHandler';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ThemeProvider } from '@/components/theme-provider';
import { es } from 'date-fns/locale';
import { setDefaultOptions } from 'date-fns';
import { Toaster } from '@/components/ui/sonner';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { Root as PortalRoot } from '@radix-ui/react-portal';

setDefaultOptions({ locale: es });
const getBasename = () => {
  return `/${import.meta.env.VITE_PUBLIC_URL?.split('/').pop() || ''}`;
};
const history = createBrowserHistory({ basename: getBasename() });
initSentry(history);
initPostHog();
// Only the manager bundle already contains modules/app/routes.tsx (via
// AppManager) — importing managerTaxonomy.ts here costs nothing extra
// there, while maquinista.jsx must never reference it (measured: +3.4MB /
// +352 precached chunks when it did — see taxonomy.ts's header comment).
registerManagerTaxonomy();

// Amplify must be configured before the first render — useCurrentIdentity()
// calls Auth.currentAuthenticatedUser() at mount, above the auth provider in
// the tree. It used to be configured in an AuthMachineProvider effect, which
// mounted AFTER the identity gate, so every boot resolved the identity as
// null first (cache never restored on a signed-in reload). Configured here,
// in the entry, rather than at amplify.ts module scope so the test
// environment (which imports amplify.ts transitively but never the entries)
// is unaffected.
listenForHostedUIFailure();
restoreDestinationAfterSignOut(history);
Amplify.configure(amplifyConfig);

if (import.meta.env.MODE !== 'production') {
  const whyDidYouRender = await import('@welldone-software/why-did-you-render');
  whyDidYouRender.default(React, {
    logOnDifferentValues: true,
    include: [],
  });
}

const container = document.getElementById('root');
const root = createRoot(container); // createRoot(container!) if you use TypeScript
root.render(
  <ThemeProvider>
    <AppErrorBoundary>
      <NuqsAdapter>
        <PortalRoot>
          <Toaster className="pointer-events-auto" />
        </PortalRoot>
        <JotaiProvider>
          <ServiceWorkerLoader />
          <Router history={history}>
            <GraphQLProvider>
              <AuthMachineProvider>
                <WebPushHandler />
                <BilderitRbacManager />
                <ShortcutsProvider>
                  <KeyboardHelpModal />
                  <InstallProvider>
                    <TooltipProvider>
                      <AppErrorBoundary>
                        <App />
                      </AppErrorBoundary>
                    </TooltipProvider>
                  </InstallProvider>
                </ShortcutsProvider>
              </AuthMachineProvider>
            </GraphQLProvider>
          </Router>
        </JotaiProvider>
      </NuqsAdapter>
    </AppErrorBoundary>
  </ThemeProvider>
);
